<template>
  <FormItem
    :id="id || name"
    :label="label"
    :label-width="labelWidth"
    :required="isRequired"
    :layout="layout"
    :error-message="errorMessage"
    :help-text="helpText"
    :no-margin="noMargin || isNested"
  >
    <template #before v-if="$slots.before">
      <slot name="before"></slot>
    </template>
    
    <CustomSelect
      :id="id || name"
      :name="name"
      :model-value="modelValue"
      :options="options"
      :placeholder="placeholder"
      :disabled="disabled"
      :size="size"
      :width="width"
      :validate-on-change="validateOnChange"
      :clearable="clearable"
      @update:model-value="$emit('update:modelValue', $event)"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
      @change="$emit('change', $event)"
      @clear="$emit('clear')"
      v-bind="$attrs"
    >
      <template #prefix v-if="$slots.prefix">
        <slot name="prefix"></slot>
      </template>
      
      <template #suffix v-if="$slots.suffix">
        <slot name="suffix"></slot>
      </template>
      
      <template #default v-if="$slots.default">
        <slot></slot>
      </template>
    </CustomSelect>
    
    <template #after v-if="$slots.after">
      <slot name="after"></slot>
    </template>
  </FormItem>
</template>

<script setup>
import { computed, inject, provide } from 'vue'
import FormItem from './FormItem.vue'
import CustomSelect from './CustomSelect.vue'

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  width: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [String, Number, Boolean, Array],
    default: ''
  },
  name: {
    type: String,
    required: true
  },
  options: {
    type: Array,
    default: () => []
  },
  label: {
    type: String,
    default: ''
  },
  labelWidth: {
    type: String,
    default: undefined
  },
  placeholder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  helpText: {
    type: String,
    default: ''
  },
  layout: {
    type: String,
    default: undefined
  },
  size: {
    type: String,
    default: '',
    validator: (value) => ['sm', 'lg', ''].includes(value)
  },
  validateOnChange: {
    type: Boolean,
    default: true
  },
  noMargin: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  }
})

defineEmits(['update:modelValue', 'blur', 'focus', 'change', 'clear'])

const formContext = inject('formContext', null)
const isNested = inject('isFormItemNested', false)
provide('isFormItemNested', true)

const layout = computed(() => props.layout || (formContext ? formContext.layout : 'vertical'))
const labelWidth = computed(() => props.labelWidth || (formContext ? formContext.labelWidth : '120px'))

const isRequired = computed(() => {
  if (!formContext || !formContext.rules || !formContext.rules[props.name]) return false
  
  const rules = formContext.rules[props.name]
  return rules.some(rule => rule.required)
})

const errorMessage = computed(() => {
  if (!formContext || !formContext.errors) return ''
  return formContext.errors[props.name] || ''
})
</script> 